import React from "react";
import { Plus, Trash2, FileText } from "lucide-react";

export interface Notebook {
  notebook_id: string;
  name: string;
  description?: string;
  created_at: string;
  cellCount: number;
  user_id: string;
  cell_count: number;
  connection_id: string;
}

interface NotebookSidebarProps {
  notebooks: Notebook[];
  currentNotebook: Notebook | null;
  sidebarOpen: boolean;
  onCreateNotebook: () => void;
  onSwitchNotebook: (notebook: Notebook) => void;
  onDeleteNotebook: (notebookId: string, e: React.MouseEvent) => void;
}

export const NotebookSidebar: React.FC<NotebookSidebarProps> = ({
  notebooks,
  currentNotebook,
  sidebarOpen,
  onCreateNotebook,
  onSwitchNotebook,
  onDeleteNotebook,
}) => {
  return (
    <div
      className={`${
        sidebarOpen ? "w-80" : "w-0"
      } transition-all duration-300 bg-gray-800 border-r border-gray-700 flex flex-col overflow-hidden flex-shrink-0`}
    >
      <div className="p-4 border-b border-gray-700 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-100">Notebooks</h2>
        </div>
        <button
          onClick={onCreateNotebook}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Notebook
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {notebooks.map((notebook) => (
          <div
            key={notebook.notebook_id}
            onClick={() => onSwitchNotebook(notebook)}
            className={`p-3 rounded-lg cursor-pointer transition-colors group ${
              currentNotebook?.notebook_id === notebook.notebook_id
                ? "bg-gray-700 border border-blue-500"
                : "bg-gray-900 border border-gray-700 hover:bg-gray-700"
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-100 truncate">
                    {notebook.name}
                  </h3>
                  {notebook.description && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                      {notebook.description}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {notebook.cell_count} cells • {notebook.created_at}
                  </p>
                </div>
              </div>
              <button
                onClick={(e) => onDeleteNotebook(notebook.notebook_id, e)}
                className="p-1 hover:bg-red-900 rounded transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                title="Delete notebook"
              >
                <Trash2 className="w-4 h-4 text-red-400" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 border-t border-gray-700 flex-shrink-0">
        <p className="text-xs text-gray-500 text-center">
          {notebooks.length} notebook{notebooks.length !== 1 ? "s" : ""} total
        </p>
      </div>
    </div>
  );
};

export default NotebookSidebar;
